import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { MixRepository } from '@asm/features/mixes/repositories/mix.repository';
import { MixResponse } from '@asm/features/mixes/models/asm-mixes.model';
import { MixAdherenceResponse } from '@asm/features/quick-order-summary/models/mix-adherence.model';
import { ProductMixLink } from '@asm/features/sync/services/sync/sync.interfaces';
import { resolveOfflineMediaSource } from '@asm/features/sync/services/sync/sync.normalizers';

@Injectable({ providedIn: 'root' })
export class DefaultLocalMixService {
  private readonly mixRepo = inject(MixRepository);
  private readonly db = inject(NgxIndexedDBService);

  /**
   * Retorna uma página de mixes lidos do IndexedDB com paginação.
   *
   * **Regra de negócio (espelhada do core-commerce):**
   * Durante a sincronização, a Generic API já aplica o filtro de B2BUnit
   * server-side — somente os mixes do cliente atual são baixados.
   * Portanto, localmente não é necessário re-filtrar por `b2bUnitUid`.
   *
   * @param page      Número da página (1-based)
   * @param pageSize  Itens por página
   */
  async getPaginatedMixes(page: number, pageSize: number): Promise<MixResponse> {
    const allMixes = await firstValueFrom(this.mixRepo.getAll());

    const safePage = Math.max(1, page);
    const safePageSize = Math.max(1, pageSize);
    const total = allMixes.length;
    const numberOfPages = Math.ceil(total / safePageSize) || 1;
    const start = (safePage - 1) * safePageSize;

    const items = allMixes.slice(start, start + safePageSize).map(mix => {
      const bannerSource = resolveOfflineMediaSource(mix.banner);

      return {
        tempId: null,
        _id: mix.code,
        createdAt: '',
        updatedAt: '',
        code: mix.code,
        description: mix.description,
        active: true,
        b2bUnitUid: null,
        banner: mix.banner
          ? {
              path: mix.banner.path,
              url: bannerSource ?? mix.banner.url,
              offlineUrl: mix.banner.offlineUrl ?? null,
            }
          : null,
      };
    });

    return {
      items,
      pagination: {
        pageSize: safePageSize,
        numberOfPages,
        totalNumberOfResults: total,
        hasNext: start + safePageSize < total,
        hasPrevious: safePage > 1,
        currentPage: safePage,
      },
    };
  }

  /**
   * Retorna todos os mixes do IndexedDB sem paginação (usado em selects e filtros).
   */
  async getAllMixes(): Promise<unknown[]> {
    return firstValueFrom(this.mixRepo.getAll());
  }

  /**
   * Calcula a aderência de um conjunto de produtos a cada mix disponível.
   *
   * **Regra de negócio (espelhada do core-commerce `POST /mix/calculate-adherence`):**
   * - Para cada mix local (já filtrado por B2BUnit durante o sync):
   *   1. Obtém o conjunto de produtos do mix via `product_mix_links`
   *   2. `totalProducts`        = quantidade de produtos no mix
   *   3. `matchedProducts`      = interseção entre os produtos do carrinho e os do mix
   *   4. `adherencePercentage`  = (matchedProducts / totalProducts) × 100  (0 se totalProducts = 0)
   *   5. `missingProducts`      = produtos do mix ausentes no carrinho
   *   6. `extraProducts`        = produtos do carrinho ausentes no mix
   *
   * @param productCodes  Códigos dos produtos do carrinho
   */
  async calculateAdherence(productCodes: string[]): Promise<MixAdherenceResponse[]> {
    const [allMixes, mixLinks] = await Promise.all([
      firstValueFrom(this.mixRepo.getAll()),
      firstValueFrom(this.db.getAll<ProductMixLink>('product_mix_links')),
    ]);

    // Constrói mapa inverso: mixCode → Set<productCode>
    const productsByMix = new Map<string, Set<string>>();
    for (const link of mixLinks) {
      const mixCode = link.mix?.code;
      if (!mixCode || !link.productCode) continue;
      if (!productsByMix.has(mixCode)) {
        productsByMix.set(mixCode, new Set());
      }
      productsByMix.get(mixCode)!.add(link.productCode);
    }

    const cartProductSet = new Set(productCodes);

    return allMixes.map(mix => {
      const mixProductSet = productsByMix.get(mix.code) ?? new Set<string>();
      const totalProducts = mixProductSet.size;
      const matchedProducts = [...mixProductSet].filter(code => cartProductSet.has(code)).length;
      const adherencePercentage = totalProducts > 0 ? Math.round((matchedProducts / totalProducts) * 100) : 0;
      const missingProducts = [...mixProductSet].filter(code => !cartProductSet.has(code));
      const extraProducts = [...cartProductSet].filter(code => !mixProductSet.has(code));

      return {
        mixCode: mix.code,
        mixDescription: mix.description,
        totalProducts,
        matchedProducts,
        adherencePercentage,
        missingProducts,
        extraProducts,
      };
    });
  }
}
